import { createTheme } from '@mui/material/styles';

// Light theme palette
const lightPalette = {
  primary: {
    main: '#90E0EF', // seafoam blue
    contrastText: '#000',
  },
  secondary: {
    main: '#F4A261', // soft coral gold
  },
  background: {
    default: '#f7f9fa',
    paper: '#fff',
  },
  text: {
    primary: '#333333',
    secondary: '#666666',
  },
};

// Dark theme palette (VS Code inspired)
const darkPalette = {
  primary: {
    main: '#90E0EF', // seafoam blue
    contrastText: '#000',
  },
  secondary: {
    main: '#F4A261', // soft coral gold
  },
  background: {
    default: '#0d1117', // VS Code dark background
    paper: '#161b22', // VS Code panel background
  },
  text: {
    primary: '#f0f6fc', // VS Code light text
    secondary: '#8b949e', // VS Code muted text
  },
  divider: '#30363d', // VS Code border color
  action: {
    hover: '#21262d', // VS Code hover state
    selected: '#1f6feb', // VS Code selection
  },
};

// Common theme configuration
const commonTheme = {
  typography: {
    h1: {
      fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h2: {
      fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h3: {
      fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h4: {
      fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h5: {
      fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h6: {
      fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    body1: {
      fontFamily: "'Inter', 'Open Sans', sans-serif",
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: "'Inter', 'Open Sans', sans-serif",
      lineHeight: 1.6,
    },
    button: {
      fontFamily: "'Inter', 'Open Sans', sans-serif",
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(144, 224, 239, 0.3)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
};

// Create theme function
export const createAppTheme = (mode) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  
  return createTheme({
    ...commonTheme,
    palette: {
      ...palette,
      mode,
    },
    components: {
      ...commonTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#161b22' : '#FFFFFF',
            color: mode === 'dark' ? '#f0f6fc' : '#333333',
            boxShadow: mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderBottom: mode === 'dark' ? '1px solid #30363d' : 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#161b22' : '#fff',
            border: mode === 'dark' ? '1px solid #30363d' : 'none',
            '&:hover': {
              boxShadow: mode === 'dark' 
                ? '0 8px 24px rgba(0, 0, 0, 0.4)' 
                : '0 8px 24px rgba(0, 0, 0, 0.12)',
              transform: 'translateY(-4px)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#21262d' : undefined,
              boxShadow: mode === 'dark' 
                ? '0 4px 12px rgba(144, 224, 239, 0.2)' 
                : '0 4px 12px rgba(144, 224, 239, 0.3)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: mode === 'dark' ? '#0d1117' : '#fff',
              borderColor: mode === 'dark' ? '#30363d' : undefined,
              '&:hover': {
                borderColor: mode === 'dark' ? '#8b949e' : undefined,
              },
              '&.Mui-focused': {
                borderColor: mode === 'dark' ? '#90E0EF' : undefined,
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? '#161b22' : '#fff',
            border: mode === 'dark' ? '1px solid #30363d' : 'none',
            boxShadow: mode === 'dark' 
              ? '0 8px 24px rgba(0, 0, 0, 0.4)' 
              : '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#21262d' : undefined,
            },
          },
        },
      },
    },
  });
};

// Chakra colors for mantra cards
export const chakraColors = {
  Root: '#E63946',
  Sacral: '#F77F00',
  Solar: '#F4D35E',
  Heart: '#A8DADC',
  Throat: '#457B9D',
  ThirdEye: '#6A4C93',
  Crown: '#DAD7CD'
};

export const backgroundDefault = '#f7f9fa';

// Default theme (light mode)
export default createAppTheme('light'); 